import C from "@/components/modal/Dialog.module.css";

import { WButton, type WButtonProps } from "@/components/button/WButton.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { ModalResult } from "@/components/modal/ModalResult.ts";
import { XModalContent, XModalOverlay, type XModalOverlayProps } from "@/components/vendor/XModalOverlay.tsx";
import { isTodoFn } from "@/dev/todoFn.ts";
import { ErrorMessage } from "@/modules/error/ErrorFallback.tsx";
import { ReportingErrorBoundary } from "@/modules/error/ReportingErrorBoundary.tsx";
import { raiseBackgroundErrorToast } from "@/modules/notifications/toast.tsx";
import { mcn, type BaseProps } from "@/utils/baseProps.ts";
import { cn, runIfFn } from "@/utils/classNames.ts";
import { Trans } from "@lingui/react/macro";
import { notNull } from "@warrenio/utils/notNull";
import { Suspense, useContext, useMemo, useState, type ReactNode } from "react";
import { Dialog, DialogTrigger, Heading, OverlayTriggerStateContext, type PressEvent } from "react-aria-components";
import invariant from "tiny-invariant";
import type { Promisable } from "type-fest";

interface WModalOverlayProps extends Omit<XModalOverlayProps, "className"> {
    children: ReactNode;
    className?: string;
}

/** Display a component in an overlay (eg. fullscreen with a gray background) */
export function WModalOverlay({ children, ...props }: WModalOverlayProps) {
    return (
        <XModalOverlay className={C.ModalOverlay} {...props}>
            <XModalContent className={cn(C.Modal, C.wide)}>
                <ModalBoundary>{children}</ModalBoundary>
            </XModalContent>
        </XModalOverlay>
    );
}

/** A mock modal overlay for stories (preserves original modal dimensions for automatic screenshots) */
export function MockModalOverlay({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(true);
    const context = useMemo(
        () => ({
            isOpen,
            setOpen: setIsOpen,
            open: () => setIsOpen(true),
            close: () => setIsOpen(false),
            toggle: () => setIsOpen((prev) => !prev),
        }),
        [isOpen],
    );

    return (
        <OverlayTriggerStateContext.Provider value={context}>
            <div className={cn(C.MockModalOverlay)}>
                <div className={cn(C.Modal, C.wide)}>
                    {isOpen ? (
                        <ModalBoundary>{children}</ModalBoundary>
                    ) : (
                        <div className="p-20" data-testid="modal-closed">
                            [Modal was closed]
                        </div>
                    )}
                </div>
            </div>
        </OverlayTriggerStateContext.Provider>
    );
}

export interface WModalProps extends WModalOverlayProps {
    button?: ReactNode;
    children: ReactNode;
}

/** Automatically trigger a modal on clicking the {@link button}. */
export function WModal({ children, button, ...props }: WModalProps) {
    return (
        <DialogTrigger>
            {button}
            <WModalOverlay {...props}>{children}</WModalOverlay>
        </DialogTrigger>
    );
}

export interface WModalContentSimpleProps extends BaseProps {
    /** Title of the dialog */
    title: ReactNode;
    /** Content */
    children: ReactNode | ((close: () => void) => ReactNode);
}

export interface WModalContentProps extends Omit<WModalContentSimpleProps, "actionButton"> {
    /** Label for the action button */
    label?: ReactNode;
    /** Extra content to display next to the buttons */
    footerNotice?: ReactNode;

    /** Action button */
    actionButton?: ReactNode | ((close: () => void) => ReactNode);
    /** Auto-focus the action button */
    autoFocus?: WButtonProps["autoFocus"];

    /**
     * An optional action to be performed when the modal is accepted.
     *
     * If the action is a promise, the modal will automatically display a loading indicator.
     *
     * If the action returns {@link ModalResult.KEEP_OPEN}, the modal will not close.
     */
    modalAction: ((e: PressEvent) => Promisable<void> | Promisable<ModalResult>) | undefined;
    isActionDisabled?: boolean;
    isActionLoading?: boolean;
}

export function WModalContentSimple({ title, children }: WModalContentSimpleProps) {
    return (
        <Dialog>
            {({ close }) => (
                <>
                    <Heading className={C.Heading} slot="title">
                        {title}
                    </Heading>

                    {runIfFn(children, close)}
                </>
            )}
        </Dialog>
    );
}

function WModalBody({ children, ...baseProps }: { children: ReactNode } & BaseProps) {
    return (
        <div {...mcn(C.Content, baseProps)}>
            <Suspense fallback={<ModalLoading />}>{children}</Suspense>
        </div>
    );
}

export interface WModalFooterProps extends BaseProps {
    children?: ReactNode;
    footerNotice?: ReactNode;
}

export function WModalFooter({ children, footerNotice, ...baseProps }: WModalFooterProps) {
    const state = notNull(useContext(OverlayTriggerStateContext), "OverlayTriggerStateContext");
    return (
        <div {...mcn(C.Footer, baseProps)}>
            {footerNotice && <div className={cn("color-error font-size-small", C.Notice)}>{footerNotice}</div>}
            <div className="flex gap-3">
                <WButton action={() => state.close()}>
                    <Trans>Close</Trans>
                </WButton>
                {children}
            </div>
        </div>
    );
}

/** Standard contents wrapper for a (modal) dialog */
export function WModalContent({
    modalAction,
    isActionDisabled,
    isActionLoading,
    autoFocus,
    footerNotice,
    label,
    children,
    className,
    ...props
}: WModalContentProps) {
    invariant(!label || modalAction, "label requires modalAction");

    async function action(close: () => void, e: PressEvent) {
        let result;
        try {
            result = await modalAction?.(e);
        } catch (e) {
            raiseBackgroundErrorToast(e, "Dialog error");
        } finally {
            if (result !== ModalResult.KEEP_OPEN) {
                close();
            }
        }
    }

    return (
        <WModalContentSimple {...props}>
            {(close) => (
                <>
                    <WModalBody className={className}>{runIfFn(children, close)}</WModalBody>
                    <WModalFooter footerNotice={footerNotice}>
                        {modalAction && (
                            <WButton
                                color="primary"
                                isDisabled={isActionDisabled}
                                isLoading={isActionLoading}
                                // Uses WButton's built-in loading indicator
                                action={async (e) => await action(close, e)}
                                isTodo={isTodoFn(modalAction)}
                                autoFocus={autoFocus}
                            >
                                {label}
                            </WButton>
                        )}
                    </WModalFooter>
                </>
            )}
        </WModalContentSimple>
    );
}

function ModalLoading() {
    return (
        <div className={C.Content}>
            <Loading icon="none" fillSpace />
        </div>
    );
}

function ModalBoundary({ children }: { children: ReactNode }) {
    return (
        <ReportingErrorBoundary FallbackComponent={ModalErrorFallback} tags="ModalBoundary">
            <Suspense fallback={<ModalLoading />}>{children}</Suspense>
        </ReportingErrorBoundary>
    );
}

function ModalErrorFallback({ error }: { error: unknown }) {
    return (
        <WModalContent
            title={
                <span className="text-error">
                    <Trans>An error happened</Trans>
                </span>
            }
            modalAction={undefined}
        >
            <ErrorMessage error={error} />
        </WModalContent>
    );
}
